import { type FC, memo, useMemo } from "react";
import { css, cx } from "@stubhub/broadway-foundation/css";
import { LegacyTagKind, LegacyTagSize, LegacyTagVariant, Tag, Text } from "@stubhub/broadway-web";
import type { FlagCountry } from "@stubhub/broadway-web/icons/flags";
import { SoccerIcon, TicketMoneyOutlineIcon } from "@stubhub/broadway-web/icons/functional";
import {
  EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_SUBTITLE_DEFAULT_PRIMARY,
  EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_SUBTITLE_DEFAULT_SECONDARY,
  EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_SUBTITLE_LOCATION_PRIORITY,
  EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_TITLE_DEFAULT,
  EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_TITLE_LOCATION_PRIORITY,
  EventGridListItemContent,
  EventGridListItemContentFieldType,
  EventGridListItemContentFieldVisibility,
  eventGridListItemContentRecipe,
  EventGridListItemSubtitle,
  EventGridListItemSubtitleGroup,
  type EventGridListItemTagDescriptor,
  EventGridListItemTags,
  EventGridListItemTitle,
  EventGridListItemTitleTeamVsTeamAbbr,
  EventGridListItemTitleTeamVsTeamFlag,
  EventGridListItemTitleTeamVsTeamLogo,
  EventGridListItemTitleTeamVsTeamNomenclature,
  useDisplayNames,
} from "@stubhub/ensemble-discovery";
import { useFeatureTernary } from "@viagogo/feature";
import { type EventListGridItem, MlbPlayoffGameLabelKind } from "src/http/types";
import { useContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PARTNER_THEME_IDS } from "src/shared/constants/PartnerThemeIds";
import { DataTestIds } from "src/shared/constants/TestEnums";
import {
  MlbPlayoffGameDetails,
  MlbPlayoffTagRow,
} from "src/website-apps/shared/components/events-grid/MlbPlayoffGameDetails";
import {
  mlbPlayoffMatchupClasses,
  mlbPlayoffMerchTagsClasses,
} from "src/website-apps/shared/components/events-grid/MlbPlayoffs.styles";
import { useMlbPlayoffGame, formatMlbCopy } from "src/website-apps/shared/components/events-grid/MlbPlayoffsContext";
import { usePartnerTheme } from "src/website-apps/shared/hooks/usePartnerTheme";
import { useSportsEventTitle } from "src/website-apps/shared/hooks/useSportsEventTitle";
import { useTournamentRoundMessage } from "src/website-apps/shared/hooks/useTournamentRoundMessage";
import { performerEventGridListItemBodyContentRecipe } from "./performer-event-grid-list-item-body-content.recipe";

const { fallbackFlagContainer, fallbackFlag, title, festivalHeadlinerSubtitle } =
  performerEventGridListItemBodyContentRecipe();
const { contentPart, onlyMobile, onlyTabletDesktop } = eventGridListItemContentRecipe({ loadingState: "idle" });
const modernizedTagsSpacing = css({ paddingBlockStart: "primitives.50" });
// The Ensemble subtitle group sets overflow hidden + ellipsis but omits nowrap — without it the
// line wraps instead of truncating; the proper fix belongs in the Ensemble group recipe.
const modernizedSubtitleLine = css({ whiteSpace: "nowrap" });

interface Props {
  /** Event data for the list item */
  event: EventListGridItem;
  /** Whether this is an NBA event */
  isNBA: boolean;
  /** Whether this is an NFL event */
  isNFL: boolean;
  /** Whether this is an NHL event */
  isNHL: boolean;
  /** Whether this is an MLB event */
  isMLB: boolean;
  /** Whether this is an MLS event */
  isMLS?: boolean;
  /** Whether this is a national team event (e.g. world cup match) */
  isNationalTeam: boolean;
  /** Whether to prioritize location information in title and subtitle rendering */
  locationPriority: boolean;
  /**
   * When true, the event carries playoff-series metadata.
   * Triggers: (1) Date prepended to primary subtitle, (2) tournamentRoundMessage suppressed.
   */
  isPlayoffEvent?: boolean;
  /** Home/Away for the featured performer's team. Null when not in experiment variant or not applicable. */
  homeOrAway?: "home" | "away" | null;
  /** backend-owned playoff card presentation for the current surface slot */
  usePlayoffPresentation?: boolean;
  /** When true, hides EventMessageTags (merchandising messages). Used by recently viewed carousel. */
  hideMerchTags?: boolean;
  /** When true, renders subtitle fields without visibility restrictions so venue info always splits into two lines. Used by recently viewed carousel where cards are narrow. */
  compactSubtitle?: boolean;
  /**
   * Controls subtitle rendering mode.
   * - "standard" (default): shows time + venue/location subtitles
   * - "ga": hides venue/location, shows mobile-only from-price instead
   */
  subtitleMode?: "standard" | "ga";
  /**
   * Composition of the standard info block (modernized event card arms).
   * - "standard" (default): time+location and venue as two stacked subtitles
   * - "two-line": weekday+time and location inline on the first line, venue on the second
   * - "single-line": time, location, and venue inline on one truncated line
   * - "location-line": location and venue on a single line — pairs with the date-time block
   */
  subtitleLayout?: "standard" | "two-line" | "single-line" | "location-line";
  /** Formatted from-price string (e.g. "$504") to show as a green tag. Used by cheapest events carousel. */
  fromPriceTag?: string | null;
  /** Default timing/state tags to render for the event */
  tags: EventGridListItemTagDescriptor[];
  /** Merchandising message tags computed by useMerchMessageTags */
  merchTags?: EventGridListItemTagDescriptor[];
  /** Headliner string for festival event cards (CWP-2723 variant 1). Null in control, variant 2, or when attribute data is unavailable. */
  festivalHeadliners?: string | null;
}

/**
 * Renders the body content (title, subtitle, tags) for Performer page event grid list items.
 *
 * This component handles league-specific rendering logic for sports event titles and
 * conditional subtitle content based on event type and league.
 *
 * # Features
 * - **League-specific titles**:
 *   - NBA/NFL/NHL: Team vs Team with abbreviations and team colors
 *   - MLB: Team vs Team with team logos
 *   - National team (e.g. World Cup): Team vs Team with team flags
 *   - Other sports/non-sports: Default title content from Ensemble
 * - **Conditional subtitles**:
 *   - MLB tournament rounds (e.g., "World Series Game 1")
 *   - Parking event indicators
 * - **Event message tags**: Merchandising messages, promotions, and event state indicators
 * - **Best selling indicators**: Highlights best-selling events with special messaging
 * - **Playoff subtitle**: Date prepended to primary subtitle;
 *   tournamentRoundMessage suppressed for playoff events to avoid duplication.
 *
 * @example
 * ```tsx
 * <EventGridListItemBody>
 *   <PerformerEventGridListItemBodyContent
 *     event={eventData}
 *     isNBA={true}
 *     isNFL={false}
 *     isNHL={false}
 *     isMLB={false}
 *     isNationalTeam={false}
 *     isPlayoffEvent={true}
 *     tags={...}
 *   />
 * </EventGridListItemBody>
 * ```
 *
 * @remarks
 * **Title rendering priority:**
 * 1. If NBA/NFL/NHL with team data → Show team abbreviations with colors
 * 2. If MLB with team logo data → Show team logos
 * 3. If `locationPriority` → Show VenueLocation + VenueName
 * 4. Otherwise → Default Ensemble title content (eventTitle from context, covers festival/support act names set by parent)
 *
 * **Subtitle rendering:**
 * - Playoff events (V1+V2): Date field prepended; tournamentRoundMessage suppressed
 * - Conditionally adds tournament round info for MLB events (non-playoff path only)
 * - Adds "(Parking passes only)" indicator for parking events
 * - Falls back to Ensemble's default subtitle components
 *
 * @see useSportsEventTitle - Extracts team data from event metadata
 * @see usePartnerTheme - Gets team logos from partner themes
 * @see useTournamentRoundMessage - Formats tournament round messages
 * @see EventMessageTags - Legacy merchandising and promotional message tags
 */
export const PerformerEventGridListItemBodyContent: FC<Props> = memo(
  ({
    event,
    isNBA,
    isNFL,
    isNHL,
    isMLB,
    isMLS = false,
    isNationalTeam,
    locationPriority,
    isPlayoffEvent = false,
    homeOrAway = null,
    usePlayoffPresentation = false,
    hideMerchTags = false,
    compactSubtitle = false,
    subtitleMode = "standard",
    subtitleLayout = "standard",
    fromPriceTag,
    tags,
    merchTags = [],
    festivalHeadliners = null,
  }) => {
    /** "Home Game \{0\}" */
    const homeGameContent = useContent("cid$#4399101#");
    /** "Game \{0\}" */
    const seriesGameContent = useContent("cid$#4399102#");
    /** "Time TBA" */
    const timeTbaContent = useContent("cid$#4399104#");
    const { getRegionDisplayName } = useDisplayNames({ style: "long" });
    const {
      homeTeamId,
      awayTeamId,
      homeTeamName,
      awayTeamName,
      homeTeamPrimaryColor,
      awayTeamPrimaryColor,
      homeTeamAbbv,
      awayTeamAbbv,
      homeTeamNationalTeamProfile,
      awayTeamNationalTeamProfile,
    } = useSportsEventTitle(event.eventMetadata);
    const homeThemePartnerId = PARTNER_THEME_IDS[homeTeamId];
    const awayThemePartnerId = PARTNER_THEME_IDS[awayTeamId];
    const { transparentPartnerLogoUrl: resolvedHomeTeamLogoSource } = usePartnerTheme(homeThemePartnerId ?? undefined);
    const { transparentPartnerLogoUrl: resolvedAwayTeamLogoSource } = usePartnerTheme(awayThemePartnerId ?? undefined);
    // With no explicit partner name, usePartnerTheme falls back to the page theme. Do not let
    // an unknown/TBD side inherit the performer's logo.
    const homeTeamLogoSource = homeThemePartnerId ? resolvedHomeTeamLogoSource : undefined;
    const awayTeamLogoSource = awayThemePartnerId ? resolvedAwayTeamLogoSource : undefined;

    /** "TBD" */
    const tbdText = useContent("cid$#3983126#");
    /** "From" */
    const fromContent = useContent("cid$#4153245#");

    /** "Home" */
    const homeContent = useContent("cid$#2944717#");
    /** "Away" */
    const awayContent = useContent("cid$#2944718#");
    const hasHomeAbbrData = !!homeTeamName && !!homeTeamAbbv && !!homeTeamPrimaryColor;
    const hasAwayAbbrData = !!awayTeamName && !!awayTeamAbbv && !!awayTeamPrimaryColor;

    const execTeamVsTeamHomeEmphasis = useFeatureTernary(
      FeatureSetEnum.TMPerformerTeamVsTeamHomeEmphasis,
      () => (isNBA || isNFL || isNHL) && (hasHomeAbbrData || hasAwayAbbrData),
    );

    const isPlayoffEventCard = usePlayoffPresentation && isPlayoffEvent && (isNBA || isNHL);
    const mlbPlayoff = useMlbPlayoffGame(event.eventId);
    const mlbPlayoffGame = mlbPlayoff?.game;
    const awayTeamLabel = mlbPlayoff?.awayTeamLabels?.get(event.eventId);
    const mlbPlayoffLabel =
      mlbPlayoffGame?.labelNumber === null || mlbPlayoffGame?.labelNumber === undefined
        ? null
        : formatMlbCopy(
            mlbPlayoffGame.labelKind === MlbPlayoffGameLabelKind.HomeGame ? homeGameContent : seriesGameContent,
            mlbPlayoffGame.labelNumber,
          );

    const isSeasonTicketEvent = event.eventMetadata?.sports?.isSeasonTicket;

    const customTitleContent = useMemo(() => {
      const homeAwayLabelText = homeOrAway === "home" ? homeContent : homeOrAway === "away" ? awayContent : null;

      const node = (() => {
        if ((isNBA || isNFL || isNHL) && (hasHomeAbbrData || hasAwayAbbrData) && !isSeasonTicketEvent) {
          // abbrMode pipe-home only applies when BOTH sides have data — the team-dot beside the
          // home team makes no sense if either team is TBD, so default back to undefined.
          // abbrMode plain only applies in variant of TMPerformerHomeAwayTextIndicator
          const abbrMode = homeOrAway
            ? ("plain" as const)
            : hasHomeAbbrData
              ? (execTeamVsTeamHomeEmphasis({
                  control: () => undefined,
                  variant: () => "pipe-home" as const,
                }) ?? undefined)
              : undefined;

          // Fall back the side that lacks abbr data to TBD values (name / 3-char abbr / neutral
          // color) so the title still renders cleanly when the matchup isn't fully known.
          return (
            <EventGridListItemTitleTeamVsTeamAbbr
              data-testid={DataTestIds.EventGridItemTeamVsTeamTitle}
              nomenclature={EventGridListItemTitleTeamVsTeamNomenclature.At}
              homeTeamShortName={homeTeamName ?? tbdText}
              homeTeamAbbr={homeTeamAbbv ?? undefined}
              homeTeamColor={homeTeamPrimaryColor ?? undefined}
              awayTeamShortName={awayTeamName ?? tbdText}
              awayTeamAbbr={awayTeamAbbv ?? undefined}
              awayTeamColor={awayTeamPrimaryColor ?? undefined}
              abbrMode={abbrMode}
            />
          );
        }

        const hasLogoData = !!homeTeamName && !!awayTeamName && !!homeTeamLogoSource && !!awayTeamLogoSource;
        if ((mlbPlayoffGame || (isMLB && hasLogoData)) && !isSeasonTicketEvent) {
          return (
            <EventGridListItemTitleTeamVsTeamLogo
              classes={mlbPlayoffGame ? mlbPlayoffMatchupClasses : undefined}
              data-testid={DataTestIds.EventGridItemTeamVsTeamTitle}
              nomenclature={EventGridListItemTitleTeamVsTeamNomenclature.At}
              homeTeamShortName={homeTeamName ?? tbdText}
              homeTeamLogoSource={homeTeamLogoSource ?? undefined}
              awayTeamShortName={awayTeamLabel ?? awayTeamName ?? tbdText}
              awayTeamLogoSource={awayTeamLabel ? undefined : (awayTeamLogoSource ?? undefined)}
            />
          );
        }

        if (isNationalTeam) {
          return (
            <EventGridListItemTitleTeamVsTeamFlag
              data-testid={DataTestIds.EventGridItemTeamVsTeamTitle}
              nomenclature={EventGridListItemTitleTeamVsTeamNomenclature.Versus}
              homeTeamShortName={
                homeTeamNationalTeamProfile?.countryCode
                  ? getRegionDisplayName(homeTeamNationalTeamProfile.countryCode)
                  : (homeTeamNationalTeamProfile?.alias ?? tbdText)
              }
              homeTeamCountryCode={homeTeamNationalTeamProfile?.countryCode as FlagCountry | undefined}
              awayTeamShortName={
                awayTeamNationalTeamProfile?.countryCode
                  ? getRegionDisplayName(awayTeamNationalTeamProfile.countryCode)
                  : (awayTeamNationalTeamProfile?.alias ?? tbdText)
              }
              awayTeamCountryCode={awayTeamNationalTeamProfile?.countryCode as FlagCountry | undefined}
              fallbackFlag={
                <span className={fallbackFlagContainer}>
                  <SoccerIcon classes={{ root: cx(fallbackFlag) }} size={12} />
                </span>
              }
            />
          );
        }

        if (isMLS && homeAwayLabelText) {
          return <EventGridListItemContent fields={EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_TITLE_DEFAULT} />;
        }

        return null;
      })();

      if (!node) return null;
      if (!homeAwayLabelText || mlbPlayoffGame) return node;
      return (
        <span>
          <span className={title}>{node}</span>
          <Text
            as="span"
            variant="body"
            size="small"
            color="text.subtle"
            className={cx(contentPart, onlyTabletDesktop, title)}
          >
            {homeAwayLabelText}
          </Text>
        </span>
      );
    }, [
      getRegionDisplayName,
      isNBA,
      isNFL,
      isNHL,
      isMLB,
      isMLS,
      isNationalTeam,
      isSeasonTicketEvent,
      homeTeamName,
      homeTeamAbbv,
      homeTeamPrimaryColor,
      homeTeamLogoSource,
      homeTeamNationalTeamProfile,
      awayTeamName,
      awayTeamAbbv,
      awayTeamPrimaryColor,
      awayTeamLogoSource,
      awayTeamNationalTeamProfile,
      tbdText,
      execTeamVsTeamHomeEmphasis,
      hasHomeAbbrData,
      hasAwayAbbrData,
      homeOrAway,
      homeContent,
      awayContent,
      mlbPlayoffGame,
      awayTeamLabel,
    ]);

    const tournamentRoundMessage = useTournamentRoundMessage(event.eventMetadata?.sports, event.isParkingEvent);
    /** "Preseason game" */
    const preseasonMessage = useContent("cid$#4220916#");
    /** "(Parking passes only)" */
    const parkingEventContent = useContent("cid$#3636236#");

    const customSubtitleContent = useMemo(() => {
      // If we're overwriting the main event title with custom content, we need to add color to the details of the event here
      if (!customTitleContent || mlbPlayoffGame) {
        return null;
      }

      const subtitleParts = [
        // Show tournament round subtitle if applicable — suppressed for playoff cards
        ...(tournamentRoundMessage && !isPlayoffEventCard && !mlbPlayoffGame ? [tournamentRoundMessage] : []),
        // Show preseason subtitle if applicable
        ...(event.name.toLocaleLowerCase().includes("preseason") ? [preseasonMessage] : []),
        // Show parking event subtitle if applicable
        ...(event.isParkingEvent ? [parkingEventContent] : []),
      ] as const satisfies readonly string[];

      if (subtitleParts.length > 0) {
        return (
          <EventGridListItemSubtitle truncate={false}>
            {subtitleParts.map((part) => (
              <span key={part} className={contentPart}>
                {part}
              </span>
            ))}
          </EventGridListItemSubtitle>
        );
      }
      return null;
    }, [
      isPlayoffEventCard,
      tournamentRoundMessage,
      preseasonMessage,
      customTitleContent,
      event.isParkingEvent,
      event.name,
      parkingEventContent,
      mlbPlayoffGame,
    ]);

    // Playoff V1+V2: prepend Date as the first field of the primary subtitle.
    const primarySubtitleFields = useMemo(() => {
      if (isPlayoffEventCard) {
        return [
          { type: EventGridListItemContentFieldType.Date },
          ...EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_SUBTITLE_DEFAULT_PRIMARY,
        ];
      }
      return EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_SUBTITLE_DEFAULT_PRIMARY;
    }, [isPlayoffEventCard]);

    const hasVisibleTags = Boolean(fromPriceTag || (!hideMerchTags && (tags.length > 0 || merchTags.length > 0)));

    const eventTags = (
      <>
        {fromPriceTag && (
          <EventGridListItemTags legacy tags={tags} classes={mlbPlayoffGame ? mlbPlayoffMerchTagsClasses : undefined}>
            <Tag
              kind={LegacyTagKind.Success}
              variant={LegacyTagVariant.Borderless}
              size={LegacyTagSize.Small}
              startIcon={TicketMoneyOutlineIcon}
            >
              {fromContent} {fromPriceTag}
            </Tag>
          </EventGridListItemTags>
        )}
        {!hideMerchTags && (!mlbPlayoffGame || tags.length > 0 || merchTags.length > 0) && (
          <EventGridListItemTags
            legacy
            tags={[...tags, ...merchTags]}
            classes={mlbPlayoffGame ? mlbPlayoffMerchTagsClasses : undefined}
          />
        )}
      </>
    );

    return (
      <>
        <EventGridListItemTitle
          data-testid={DataTestIds.EventGridItemTitleText}
          truncate={subtitleLayout !== "standard"}
        >
          {customTitleContent ??
            (locationPriority ? (
              <EventGridListItemContent fields={EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_TITLE_LOCATION_PRIORITY} />
            ) : (
              <EventGridListItemContent fields={EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_TITLE_DEFAULT} />
            ))}
        </EventGridListItemTitle>
        {festivalHeadliners && (
          <Text as="div" className={festivalHeadlinerSubtitle!} size="small" variant="body" truncate>
            {festivalHeadliners}
          </Text>
        )}
        {customSubtitleContent}
        {mlbPlayoffGame ? (
          <MlbPlayoffGameDetails
            label={mlbPlayoffLabel}
            timeLabel={event.isTimeConfirmed ? undefined : timeTbaContent}
          />
        ) : locationPriority ? (
          <EventGridListItemSubtitle>
            <EventGridListItemContent fields={EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_SUBTITLE_LOCATION_PRIORITY} />
          </EventGridListItemSubtitle>
        ) : compactSubtitle ? (
          <>
            <EventGridListItemSubtitle>
              <EventGridListItemContent
                fields={[
                  { type: EventGridListItemContentFieldType.Time },
                  { type: EventGridListItemContentFieldType.VenueName },
                ]}
              />
            </EventGridListItemSubtitle>
            <EventGridListItemSubtitle>
              <EventGridListItemContent fields={[{ type: EventGridListItemContentFieldType.VenueLocation }]} />
            </EventGridListItemSubtitle>
          </>
        ) : subtitleLayout === "location-line" ? (
          <EventGridListItemSubtitle>
            <EventGridListItemContent
              fields={[
                { type: EventGridListItemContentFieldType.VenueLocation },
                { type: EventGridListItemContentFieldType.VenueName },
              ]}
            />
          </EventGridListItemSubtitle>
        ) : subtitleLayout === "single-line" ? (
          <EventGridListItemSubtitleGroup classes={{ group: modernizedSubtitleLine }}>
            <EventGridListItemSubtitle inline>
              <EventGridListItemContent fields={[{ type: EventGridListItemContentFieldType.Time }]} />
            </EventGridListItemSubtitle>
            <EventGridListItemSubtitle inline>
              <EventGridListItemContent
                fields={[
                  { type: EventGridListItemContentFieldType.VenueLocation },
                  { type: EventGridListItemContentFieldType.VenueName },
                ]}
              />
            </EventGridListItemSubtitle>
          </EventGridListItemSubtitleGroup>
        ) : subtitleLayout === "two-line" ? (
          <>
            <EventGridListItemSubtitleGroup classes={{ group: modernizedSubtitleLine }}>
              <EventGridListItemSubtitle inline>
                <EventGridListItemContent
                  fields={[
                    { type: EventGridListItemContentFieldType.DayOfWeek },
                    { type: EventGridListItemContentFieldType.Time },
                  ]}
                />
              </EventGridListItemSubtitle>
              <EventGridListItemSubtitle inline>
                <EventGridListItemContent
                  fields={[
                    { type: EventGridListItemContentFieldType.VenueLocation },
                    {
                      type: EventGridListItemContentFieldType.VenueName,
                      visibility: EventGridListItemContentFieldVisibility.TabletDesktop,
                    },
                  ]}
                />
              </EventGridListItemSubtitle>
            </EventGridListItemSubtitleGroup>
            <EventGridListItemSubtitle>
              <EventGridListItemContent
                fields={[
                  {
                    type: EventGridListItemContentFieldType.VenueName,
                    visibility: EventGridListItemContentFieldVisibility.Mobile,
                  },
                ]}
              />
            </EventGridListItemSubtitle>
          </>
        ) : subtitleMode === "ga" ? null : (
          <>
            <EventGridListItemSubtitle>
              <EventGridListItemContent fields={primarySubtitleFields} />
            </EventGridListItemSubtitle>
            <EventGridListItemSubtitle>
              {homeOrAway && (
                <span className={cx(contentPart, onlyMobile)}>{homeOrAway === "home" ? homeContent : awayContent}</span>
              )}
              <EventGridListItemContent fields={EVENT_GRID_LIST_ITEM_CONTENT_FIELDS_SUBTITLE_DEFAULT_SECONDARY} />
            </EventGridListItemSubtitle>
          </>
        )}
        {mlbPlayoffGame && hasVisibleTags ? (
          <MlbPlayoffTagRow>{eventTags}</MlbPlayoffTagRow>
        ) : subtitleLayout !== "standard" && hasVisibleTags ? (
          <div className={modernizedTagsSpacing} data-testid={DataTestIds.EventGridItemModernizedTagsContainer}>
            {eventTags}
          </div>
        ) : (
          eventTags
        )}
      </>
    );
  },
);
PerformerEventGridListItemBodyContent.displayName = "PerformerEventGridListItemBodyContent";
